import { useEffect, useState, useMemo, useCallback } from 'react';
import { ConfigProvider, Spin } from 'antd';
import { UploadFileHandler } from '@components/Handlers/UploadFileHandler/Index.jsx';
import { CustomLoader } from '@components/UI/CustomLoader/Index.jsx';
import ptBR from 'antd/es/locale/pt_BR';
import { StyledTableItensProcessoSolicitacao } from '@styles/datatables/datatable-itens-processo-solicitacao.js';
import { BlockedOptionComp } from '@components/UI/BlockedOption/Index.jsx';
import TextBlock from "@components/UI/TextBlock/Index..jsx";
import ObservacoesHandler from '@components/Handlers/ObservacoesHandler/Index.jsx';
import { useDispatch } from 'react-redux';
import { carregarObservacoesIniciais } from '@store/slices/observacoesSlice.js';
import { FormularioOptionItemProcessoModal } from '@components/Modals/User/FormularioOptionItemProcessoModal/Index';
import { carregarDocumentos } from '@store/slices/documentosSlice';

const GridEditItensProcessoSolicitacao = ({
  solicitacaoProcessoItems = [],
  onUpdateSolicitacaoProcessoItems,
  idProcesso
}) => {
  const [loading, setLoading] = useState(true);
  const [sortedData, setSortedData] = useState([]);
  const dispatch = useDispatch();

  useEffect(() => {
    if (solicitacaoProcessoItems && solicitacaoProcessoItems.length > 0) {
      const ordered = solicitacaoProcessoItems
        .filter(item => item.flagSelecionado)
        .sort((a, b) => a.nomeItem.localeCompare(b.nomeItem));

      setSortedData(ordered);
      setLoading(false); // Finaliza o loading ao preparar os dados

      dispatch(carregarObservacoesIniciais(ordered));

      ordered.forEach(item => {
        dispatch(carregarDocumentos({
          idProcessoItem: item.id,
          documentos: item.documentos || []
        }));
      });
    } else {
      setSortedData([]);
      setLoading(false); // Mesmo que a lista venha vazia, encerra o loading
    }
  }, [solicitacaoProcessoItems, dispatch]);

  const handleSelectOption = useCallback((idProcessoItem, value) => {
    const updated = sortedData.map((item) =>
      item.idProcessoItem === idProcessoItem
        ? { ...item, formulario: value }
        : item
    );
    setSortedData(updated);
    onUpdateSolicitacaoProcessoItems(updated);
  }, [sortedData, onUpdateSolicitacaoProcessoItems]);

  const columns = useMemo(() => [
    {
      title: 'Nome do Item',
      dataIndex: 'nomeItem',
      key: 'nomeItem',
      width: '55%',
      align: 'center',
      sorter: (a, b) => a.nomeItem.localeCompare(b.nomeItem),
      render: (text, record) => <TextBlock>{text}</TextBlock>,
    },
    {
      title: 'Observações',
      key: 'observacoes',
      width: '12%',
      render: (record) => {
        const nomeItemProcesso = record.nomeItem;

        return record.flagSelecionado ? (
          <div className="d-flex align-items-center gap-2 justify-content-center">
            <ObservacoesHandler
              row={record}
              nomeItemProcesso={nomeItemProcesso}
            />
          </div>
        ) : (
          <BlockedOptionComp title={"Selecione o item para acessar"} />
        );
      },
    },
    {
      title: 'Formulário',
      key: 'formulario',
      width: '12%',
      render: (record) =>
        record.flagSelecionado ? (
          <div className="d-flex justify-content-center gap-2">
            <FormularioOptionItemProcessoModal
              idFormularioAssociado={record.formularioAssociadoItemProcesso?.id}
              idSolicitacaoProcessoItem={record.id}
              idProcesso={idProcesso}
              idSolicitacao={record.idSolicitacao}
              formularioRespostaInicial={record.formularioRespostaItemProcesso}
            />
          </div>
        ) : (
          <BlockedOptionComp title={"Selecione o item para acessar"} />
        ),
    },
    {
      title: 'Documentos',
      key: 'documentos',
      align: 'center',
      width: '12%',
      render: (record) => {
        const docs = record.documentos || [];

        return record.flagSelecionado ? (
          <div className="d-flex align-items-center gap-2 justify-content-center">
            <UploadFileHandler
              row={record}
              existingDocuments={docs}
            />
          </div>
        ) : (
          <BlockedOptionComp title={"Selecione o item para acessar"} />
        );
      },
    },
  ], [sortedData, handleSelectOption]);

  const rowSelection = useMemo(() => ({
    columnTitle: '-',
    selectedRowKeys: sortedData
      .filter(item => item.flagSelecionado)
      .map(item => item.idProcessoItem),
    onChange: () => { }, // impede alteração
    preserveSelectedRowKeys: true,
    getCheckboxProps: () => ({
      disabled: true, // desabilita o checkbox de seleção
    }),
  }), [sortedData]);

  const selectedCount = rowSelection.selectedRowKeys.length;

  return (
    <div className="mt-1">
      {loading && <CustomLoader />}
      <div className="mt-3 text-start">
        <h1>
          <strong>{selectedCount}</strong>{' '}
          {selectedCount === 1 ? 'item selecionado' : 'itens selecionados'}
        </h1>
      </div>
      <div className="mt-4">
        <ConfigProvider locale={ptBR}>
          <StyledTableItensProcessoSolicitacao
            rowKey="idProcessoItem"
            loading={{
              spinning: loading,
              text: 'Carregando...',
              indicator: <Spin />
            }}
            columns={columns}
            dataSource={sortedData}
            rowSelection={rowSelection}
            pagination={{
              defaultPageSize: 5,
              showSizeChanger: true,
              pageSizeOptions: [5, 10, 20, 50, 100],
              showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`,
            }}
            locale={{
              emptyText: <h3 className="m-3">Nenhum dado disponível para exibição.</h3>,
            }}
            scroll={{ x: 'max-content' }}
          />
        </ConfigProvider>
      </div>
    </div>
  );
};

export default GridEditItensProcessoSolicitacao;
